import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { challengesAPI, paymentsAPI, couponsAPI, platformAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Check, Loader2, RefreshCw, TrendingUp, Shield, Target, Zap, Tag, X, Sparkles } from 'lucide-react';

const Challenges = () => {
  const [challengePackages, setChallengePackages] = useState([]);
  const [instantPackages, setInstantPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [upgradeProfitSplit, setUpgradeProfitSplit] = useState({});
  const [payoutOnDemand, setPayoutOnDemand] = useState({});
  const [platformSettings, setPlatformSettings] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
    fetchPlatformSettings();
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      const response = await platformAPI.getSettings();
      setPlatformSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch platform settings');
    }
  };

  const fetchPackages = async () => {
    try {
      const [challengeRes, instantRes] = await Promise.all([
        challengesAPI.getPackages(),
        challengesAPI.getInstantPackages()
      ]);
      setChallengePackages(challengeRes.data);
      setInstantPackages(instantRes.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCoupon = async (packageId) => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const response = await couponsAPI.validate(couponCode.toUpperCase(), packageId);
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        setSelectedPackage(packageId);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
        setAppliedCoupon(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const clearCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setSelectedPackage(null);
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      toast.info('Please create an account first');
      navigate('/register');
      return;
    }

    setPurchasing(packageId);
    try {
      const originUrl = window.location.origin;
      const coupon = selectedPackage === packageId ? couponCode : null;
      const upgrade = upgradeProfitSplit[packageId] || false;
      const onDemand = payoutOnDemand[packageId] || false;
      const response = await paymentsAPI.createCheckout(packageId, originUrl, coupon, upgrade, onDemand);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  const getDisplayPrice = (pkg) => {
    let basePrice = pkg.price;
    
    // Apply coupon discount if applicable
    if (appliedCoupon && selectedPackage === pkg.package_id) {
      basePrice = appliedCoupon.final_price;
    }
    
    // Add upgrade fee if selected (+25%)
    if (upgradeProfitSplit[pkg.package_id]) {
      basePrice += pkg.price * 0.25;
    }
    
    // Add payout on demand fee if selected (+15%)
    if (payoutOnDemand[pkg.package_id]) {
      basePrice += pkg.price * 0.15;
    }
    
    return basePrice;
  };

  const getUpgradeFee = (price) => {
    return price * 0.25;
  };

  const getPayoutOnDemandFee = (price) => {
    return price * 0.15;
  };

  const toggleUpgrade = (packageId) => {
    setUpgradeProfitSplit(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  const togglePayoutOnDemand = (packageId) => {
    setPayoutOnDemand(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  // Extract rules from platform settings or use defaults
  const tradingRules = platformSettings?.trading_rules || {
    profit_target_phase1: 10,
    profit_target_phase2: 5,
    max_daily_loss: 4,
    max_total_loss: 8
  };
  
  const payoutSettingsData = platformSettings?.payout_settings || {
    profit_split_base: 100
  };

  const challengeRules = [
    { icon: Target, label: 'Phase 1 Target', value: `${tradingRules.profit_target_phase1}%` },
    { icon: Shield, label: 'Phase 2 Target', value: `${tradingRules.profit_target_phase2}%` },
    { icon: TrendingUp, label: 'Max Daily Loss', value: `${tradingRules.max_daily_loss}%` },
    { icon: TrendingUp, label: 'Max Drawdown', value: `${tradingRules.max_total_loss}%` }
  ];

  const instantRules = [
    { icon: Zap, label: 'Instant Funding', value: 'No Evaluation' },
    { icon: TrendingUp, label: 'Max Daily Loss', value: `${tradingRules.max_daily_loss}%` },
    { icon: TrendingUp, label: 'Max Drawdown', value: `${tradingRules.max_total_loss}%` },
    { icon: Shield, label: 'Profit Split', value: `${payoutSettingsData.profit_split_base}%` }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 md:mb-12"
        >
          <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
            Choose Your Account
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight mb-4 text-white" data-testid="challenges-title">
            GET <span className="gradient-text">FUNDED</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
            Choose your path to funding. Complete our evaluation or get instant access to a funded account.
          </p>
        </motion.div>

        {/* Coupon Code Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8"
        >
          <Card className="bg-[hsl(220,20%,7%)] border border-white/10 shadow-lg max-w-md mx-auto">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-sm font-medium text-gray-300">Have a coupon?</span>
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500 uppercase rounded-lg"
                  data-testid="coupon-input"
                />
                {appliedCoupon ? (
                  <Button variant="outline" onClick={clearCoupon} className="border-white/10 text-white hover:bg-white/5" data-testid="clear-coupon">
                    <X className="w-4 h-4" />
                  </Button>
                ) : null}
              </div>
              {appliedCoupon && (
                <div className="mt-2 text-sm text-green-400 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {appliedCoupon.discount_percent}% off - Save ${appliedCoupon.discount_amount}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* FREE RETAKE Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-blue-600/20 border border-blue-500/30 shadow-xl overflow-hidden relative">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
            </div>
            <CardContent className="p-6 md:p-8 relative z-10">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 backdrop-blur border border-blue-500/30 flex items-center justify-center">
                    <RefreshCw className="w-8 h-8 text-blue-400" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="font-heading text-2xl md:text-3xl font-black text-white">
                      FREE RETAKE INCLUDED!
                    </h3>
                    <p className="text-gray-300 text-sm md:text-base">
                      First-time buyers get a <span className="font-bold text-cyan-400">complimentary retry</span> if they don't pass
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur rounded-full px-4 py-2 border border-green-500/30">
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-white font-bold text-sm">No Extra Cost</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs for Challenge vs Instant */}
        <Tabs defaultValue="challenge" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-[hsl(220,20%,7%)] border border-white/10">
            <TabsTrigger value="challenge" className="font-heading uppercase data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" data-testid="tab-challenge">
              Evaluation
            </TabsTrigger>
            <TabsTrigger value="instant" className="font-heading uppercase data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400" data-testid="tab-instant">
              Instant Funding
            </TabsTrigger>
          </TabsList>

          {/* Challenge Packages */}
          <TabsContent value="challenge">
            {/* Rules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <Card className="bg-[hsl(220,20%,7%)] border border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg uppercase text-white">Two-Phase Evaluation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {challengeRules.map((rule, i) => (
                      <div key={rule.label} className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5" data-testid={`rule-${i}`}>
                        <rule.icon className="w-4 h-4 text-blue-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500 truncate">{rule.label}</div>
                          <div className="font-mono font-bold text-white">{rule.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {user?.is_first_time_buyer && (
                    <div className="mt-4 p-3 rounded bg-blue-500/10 border border-blue-500/30 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="text-sm text-blue-300">FREE RETAKE included with your first purchase!</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Challenge Packages Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {challengePackages.map((pkg, i) => (
                <motion.div
                  key={pkg.package_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                >
                  <Card 
                    className={`h-full bg-[hsl(220,20%,7%)] border transition-all duration-300 relative overflow-visible ${
                      pkg.package_id === '50k' ? 'border-blue-500/50 glow-blue mt-3' : 'border-white/10 hover:border-blue-500/30'
                    }`}
                    data-testid={`package-${pkg.package_id}`}
                  >
                    {pkg.package_id === '50k' && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-cyan-500 text-xs z-10">
                        POPULAR
                      </Badge>
                    )}
                    <CardContent className="p-4 flex flex-col h-full">
                      <div className="text-center mb-4">
                        <h3 className="font-heading font-bold text-xl md:text-2xl mb-1 text-white">
                          {formatCurrency(pkg.account_size)}
                        </h3>
                        <div className="font-mono text-xl text-blue-400 font-bold">
                          {formatCurrency(getDisplayPrice(pkg))}
                          {(appliedCoupon && selectedPackage === pkg.package_id) || upgradeProfitSplit[pkg.package_id] || payoutOnDemand[pkg.package_id] ? (
                            <span className="line-through text-gray-500 text-sm ml-1">
                              ${pkg.price}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="space-y-2 flex-1 text-xs md:text-sm text-gray-300">
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-blue-400 shrink-0" />
                          <span>{pkg.profit_target_phase1 || 10}% Phase 1</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-blue-400 shrink-0" />
                          <span>{pkg.profit_target_phase2 || 5}% Phase 2</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-blue-400 shrink-0" />
                          <span className={upgradeProfitSplit[pkg.package_id] ? 'text-green-400 font-semibold' : ''}>
                            {upgradeProfitSplit[pkg.package_id] ? '100% Split' : 'Up to 100% Split'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-blue-400 shrink-0" />
                          <span className={payoutOnDemand[pkg.package_id] ? 'text-cyan-400 font-semibold' : ''}>
                            {payoutOnDemand[pkg.package_id] ? 'Withdraw Anytime' : 'First payout in 6 days'}
                          </span>
                        </div>
                      </div>

                      {/* Profit Split Upgrade Option */}
                      <div 
                        className={`mt-3 p-2 rounded-lg border cursor-pointer transition-all ${
                          upgradeProfitSplit[pkg.package_id] 
                            ? 'bg-green-500/10 border-green-500/50' 
                            : 'bg-white/5 border-white/10 hover:border-green-500/30'
                        }`}
                        onClick={() => toggleUpgrade(pkg.package_id)}
                        data-testid={`upgrade-${pkg.package_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={upgradeProfitSplit[pkg.package_id] || false}
                            onCheckedChange={() => toggleUpgrade(pkg.package_id)}
                            className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-green-400" />
                              <span className="text-xs font-medium text-white">100% Profit Split</span>
                            </div>
                            <p className="text-[10px] text-gray-500">
                              +${getUpgradeFee(pkg.price).toFixed(0)} (+25%)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Payout on Demand Option */}
                      <div 
                        className={`mt-2 p-2 rounded-lg border cursor-pointer transition-all ${
                          payoutOnDemand[pkg.package_id] 
                            ? 'bg-cyan-500/10 border-cyan-500/50' 
                            : 'bg-white/5 border-white/10 hover:border-cyan-500/30'
                        }`}
                        onClick={() => togglePayoutOnDemand(pkg.package_id)}
                        data-testid={`payout-demand-${pkg.package_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={payoutOnDemand[pkg.package_id] || false}
                            onCheckedChange={() => togglePayoutOnDemand(pkg.package_id)}
                            className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-1">
                              <Zap className="w-3 h-3 text-cyan-400" />
                              <span className="text-xs font-medium text-white">Payout on Demand</span>
                            </div>
                            <p className="text-[10px] text-gray-500">
                              +${getPayoutOnDemandFee(pkg.price).toFixed(0)} (+15%) • Withdraw anytime
                            </p>
                          </div>
                        </div>
                      </div>

                      {couponCode && !appliedCoupon && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-3 text-xs border-white/10 text-white hover:bg-white/5"
                          onClick={() => handleValidateCoupon(pkg.package_id)}
                          disabled={validatingCoupon}
                        >
                          {validatingCoupon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply Coupon'}
                        </Button>
                      )}

                      <Button
                        className="w-full mt-3 font-heading uppercase tracking-wider text-xs md:text-sm bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                        onClick={() => handlePurchase(pkg.package_id)}
                        disabled={purchasing === pkg.package_id}
                        data-testid={`buy-${pkg.package_id}`}
                      >
                        {purchasing === pkg.package_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'GET STARTED'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Instant Funding Packages */}
          <TabsContent value="instant">
            {/* Rules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <Card className="bg-[hsl(220,20%,7%)] border border-cyan-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-white">
                    <Zap className="w-5 h-5 text-cyan-400" />
                    Skip The Evaluation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {instantRules.map((rule, i) => (
                      <div key={rule.label} className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                        <rule.icon className="w-4 h-4 text-cyan-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500 truncate">{rule.label}</div>
                          <div className="font-mono font-bold text-white">{rule.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-gray-400">
                    Get funded immediately without completing any evaluation. Start trading and earning profits right away.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Instant Packages Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {instantPackages.map((pkg, i) => (
                <motion.div
                  key={pkg.package_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                >
                  <Card 
                    className="h-full bg-[hsl(220,20%,7%)] border border-cyan-500/30 hover:border-cyan-500/50 transition-all duration-300"
                    data-testid={`package-${pkg.package_id}`}
                  >
                    <CardContent className="p-4 flex flex-col h-full">
                      <Badge className="mb-2 bg-cyan-500/20 text-cyan-400 border-cyan-500/30 w-fit">
                        <Zap className="w-3 h-3 mr-1" />
                        INSTANT
                      </Badge>
                      <div className="text-center mb-4">
                        <h3 className="font-heading font-bold text-xl md:text-2xl mb-1 text-white">
                          {formatCurrency(pkg.account_size)}
                        </h3>
                        <div className="font-mono text-xl text-cyan-400 font-bold">
                          {formatCurrency(getDisplayPrice(pkg))}
                          {(appliedCoupon && selectedPackage === pkg.package_id) || upgradeProfitSplit[pkg.package_id] || payoutOnDemand[pkg.package_id] ? (
                            <span className="line-through text-gray-500 text-sm ml-1">
                              ${pkg.price}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="space-y-2 flex-1 text-xs md:text-sm text-gray-300">
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-cyan-400 shrink-0" />
                          <span>No Evaluation</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-cyan-400 shrink-0" />
                          <span>Instant Access</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-cyan-400 shrink-0" />
                          <span className={upgradeProfitSplit[pkg.package_id] ? 'text-green-400 font-semibold' : ''}>
                            {upgradeProfitSplit[pkg.package_id] ? '100% Split' : `${pkg.profit_split}% Split`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Check className="w-3 h-3 text-cyan-400 shrink-0" />
                          <span className={payoutOnDemand[pkg.package_id] ? 'text-cyan-400 font-semibold' : ''}>
                            {payoutOnDemand[pkg.package_id] ? 'Withdraw Anytime' : 'First payout in 4 days'}
                          </span>
                        </div>
                      </div>

                      {/* Profit Split Upgrade Option */}
                      <div 
                        className={`mt-3 p-2 rounded-lg border cursor-pointer transition-all ${
                          upgradeProfitSplit[pkg.package_id] 
                            ? 'bg-green-500/10 border-green-500/50' 
                            : 'bg-white/5 border-white/10 hover:border-green-500/30'
                        }`}
                        onClick={() => toggleUpgrade(pkg.package_id)}
                        data-testid={`upgrade-${pkg.package_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={upgradeProfitSplit[pkg.package_id] || false}
                            onCheckedChange={() => toggleUpgrade(pkg.package_id)}
                            className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-green-400" />
                              <span className="text-xs font-medium text-white">100% Profit Split</span>
                            </div>
                            <p className="text-[10px] text-gray-500">
                              +${getUpgradeFee(pkg.price).toFixed(0)} (+25%)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Payout on Demand Option */}
                      <div 
                        className={`mt-2 p-2 rounded-lg border cursor-pointer transition-all ${
                          payoutOnDemand[pkg.package_id] 
                            ? 'bg-cyan-500/10 border-cyan-500/50' 
                            : 'bg-white/5 border-white/10 hover:border-cyan-500/30'
                        }`}
                        onClick={() => togglePayoutOnDemand(pkg.package_id)}
                        data-testid={`payout-demand-${pkg.package_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            checked={payoutOnDemand[pkg.package_id] || false}
                            onCheckedChange={() => togglePayoutOnDemand(pkg.package_id)}
                            className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-1">
                              <Zap className="w-3 h-3 text-cyan-400" />
                              <span className="text-xs font-medium text-white">Payout on Demand</span>
                            </div>
                            <p className="text-[10px] text-gray-500">
                              +${getPayoutOnDemandFee(pkg.price).toFixed(0)} (+15%) • Withdraw anytime
                            </p>
                          </div>
                        </div>
                      </div>

                      {couponCode && !appliedCoupon && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-3 text-xs border-white/10 text-white hover:bg-white/5"
                          onClick={() => handleValidateCoupon(pkg.package_id)}
                          disabled={validatingCoupon}
                        >
                          {validatingCoupon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply Coupon'}
                        </Button>
                      )}

                      <Button
                        className="w-full mt-3 font-heading uppercase tracking-wider text-xs md:text-sm bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                        onClick={() => handlePurchase(pkg.package_id)}
                        disabled={purchasing === pkg.package_id}
                        data-testid={`buy-${pkg.package_id}`}
                      >
                        {purchasing === pkg.package_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'GET FUNDED NOW'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 md:mt-12 text-center text-xs md:text-sm text-gray-500"
        >
          <p>All prices are in USD. Payments are processed securely via Stripe.</p>
          <p className="mt-2">Have questions? Check our <a href="/faq" className="text-blue-400 hover:underline">FAQ</a></p>
        </motion.div>
      </div>
    </div>
  );
};

export default Challenges;
